import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { saveState, loadState } from '../utils/storage';

const QuizContext = createContext();

const initialState = {
  currentState: 'ny', // Default state
  currentCategory: 'general',
  currentLanguage: 'en', // Default language
  currentQuestion: 0,
  answers: {},
  revealedAnswers: {}, // Track which answers are revealed
  isComplete: false,
  score: 0,
  bookmarkedQuestions: [], // For storing bookmarked questions
  quizData: null // Will store the current quiz questions
};

// Load saved state or use initial state
const getInitialState = () => {
  const savedState = loadState();
  return savedState ? { ...initialState, ...savedState } : initialState;
};

function quizReducer(state, action) {
  let newState;

  switch (action.type) {
    case 'SET_STATE':
      newState = {
        ...state,
        currentState: action.state,
        currentQuestion: 0,
        answers: {},
        revealedAnswers: {},
        isComplete: false,
        score: 0
      };
      break;
    case 'SET_LANGUAGE':
      newState = {
        ...state,
        currentLanguage: action.language
      };
      break;
    case 'SET_CATEGORY':
      newState = {
        ...state,
        currentCategory: action.category,
        currentQuestion: 0,
        answers: {},
        revealedAnswers: {},
        isComplete: false,
        score: 0
      };
      break;
    case 'SET_QUIZ_DATA':
      newState = {
        ...state,
        quizData: action.data
      };
      break;
    case 'ANSWER_QUESTION':
      newState = {
        ...state,
        answers: {
          ...state.answers,
          [action.questionId]: action.answer
        }
      };
      break;
    case 'REVEAL_ANSWER':
      newState = {
        ...state,
        revealedAnswers: {
          ...state.revealedAnswers,
          [action.questionId]: true
        }
      };
      break;
    case 'NEXT_QUESTION':
      newState = {
        ...state,
        currentQuestion: state.currentQuestion + 1
      };
      break;
    case 'PREVIOUS_QUESTION':
      newState = {
        ...state,
        currentQuestion: Math.max(0, state.currentQuestion - 1)
      };
      break;
    case 'COMPLETE_QUIZ':
      newState = {
        ...state,
        isComplete: true,
        score: action.score
      };
      break;
    case 'BOOKMARK_QUESTION':
      newState = {
        ...state,
        bookmarkedQuestions: [...state.bookmarkedQuestions, action.questionId]
      };
      break;
    case 'REMOVE_BOOKMARK':
      newState = {
        ...state,
        bookmarkedQuestions: state.bookmarkedQuestions.filter(id => id !== action.questionId)
      };
      break;
    case 'RESET_QUIZ':
      newState = {
        ...state,
        currentQuestion: 0,
        answers: {},
        revealedAnswers: {},
        isComplete: false,
        score: 0
      };
      break;
    default:
      return state;
  }

  // Save state after each action
  saveState(newState);
  return newState;
}

export function QuizProvider({ children }) {
  const [state, dispatch] = useReducer(quizReducer, getInitialState());

  // Save state whenever it changes
  useEffect(() => {
    saveState(state);
  }, [state]);

  return (
    <QuizContext.Provider value={{ state, dispatch }}>
      {children}
    </QuizContext.Provider>
  );
}

export function useQuiz() {
  const context = useContext(QuizContext);
  if (!context) {
    throw new Error('useQuiz must be used within a QuizProvider');
  }
  return context;
}
